/**
 * Profiles where the "suggest another doctor" surfaces (profile card under the no-booking notice,
 * booking-page «نوبت پر شده» modal) list other same-city specialists that can be booked now instead
 * of an online visit. Per-profile on purpose (owner requests 2026-10-19: Dr. Taheri, Dr. Asilian,
 * Dr. Norouzi) —
 * every other profile keeps the online-visit suggestion.
 */
export interface LocalPrivateSpecialistsConfig {
  /** Search city slug (route segment), e.g. `shahrekord`. */
  city: string;
  cityName: string;
  /** Search category slug (route segment), e.g. `imaging`. */
  groupSlug: string;
  /** Specialty shown in the heading, e.g. «رادیولوژی». */
  expertiseName: string;
  /** Hits whose `display_expertise` mentions none of these are dropped (other specialties in the group). */
  includeExpertise: string[];
  /** Hits whose `display_expertise` matches any of these are dropped (e.g. dental radiology, residents). */
  excludeExpertise?: string[];
  /** Search only private offices («پزشکان مطبی»). Default true; false = clinics and hospitals too. */
  privateOfficesOnly?: boolean;
  /**
   * Big cities: the city route also returns the province's towns. A doctor qualifies when one of
   * their centers has coordinates within `radiusKm` of `lat`/`lon`, or — for centers without usable
   * coordinates (missing, the 1/1 placeholder, or absurdly far) — when its `city_name` is `cityName`.
   * Only used to keep the list in the city; distance is never shown.
   */
  near?: { lat: number; lon: number; radiusKm: number };
  /** Quality floor: search satisfaction (%) and number of reviews (`rates_count`). */
  minSatisfaction?: number;
  minRates?: number;
  /** `first-turn`: earliest free turn first, then satisfaction. Default `satisfaction` (smoothed). */
  order?: 'satisfaction' | 'first-turn';
  /** Also show the suggestion while this doctor's own booking is open (under the booking card). */
  showWhenBookable?: boolean;
  /** Search result pages to scan (the API serves at most 50 per page). Default 1. */
  searchPages?: number;
}

export const LOCAL_PRIVATE_SPECIALISTS: Record<string, LocalPrivateSpecialistsConfig> = {
  'دکتر-عبدالمجید-طاهری-0': {
    city: 'shahrekord',
    cityName: 'شهرکرد',
    groupSlug: 'imaging',
    expertiseName: 'رادیولوژی',
    includeExpertise: ['متخصص رادیولوژی'],
    excludeExpertise: ['دهان و فک'],
  },
  // Dermatologist in Isfahan, often fully booked. Any center type, specialists only (residents have
  // no office licence), well rated, earliest turn first, within ~10 km of his Chaharbagh Bala office
  // (his profile has no coordinates; the point is the mean of the neighbouring Chaharbagh Bala
  // offices). 2026-10-19: 167 dermatology doctors in 4 search pages, ~15 qualified and bookable.
  'دکتر-علی-اصیلیان': {
    city: 'isfahan',
    cityName: 'اصفهان',
    groupSlug: 'dermatology',
    expertiseName: 'پوست',
    includeExpertise: ['متخصص بیماریهای پوست'],
    excludeExpertise: ['دانشجو'],
    privateOfficesOnly: false,
    near: { lat: 32.6275, lon: 51.665, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    searchPages: 4,
  },
  // Orthopedist in Malayer (Fakhrieh clinic + Imam Hossein hospital clinic), often fully booked. Same
  // rules as Dr. Asilian; the point is the Fakhrieh clinic's own coordinates. The malayer route returns
  // the whole Hamadan province (58 doctors on 2026-10-19, only 6 orthopedists in Malayer itself).
  'دکتر-سعید-نوروزی-0': {
    city: 'malayer',
    cityName: 'ملایر',
    groupSlug: 'orthopedics',
    expertiseName: 'ارتوپدی',
    includeExpertise: ['متخصص ارتوپدی'],
    excludeExpertise: ['دانشجو'],
    privateOfficesOnly: false,
    near: { lat: 34.299262, lon: 48.826077, radiusKm: 10 },
    minSatisfaction: 80,
    minRates: 10,
    order: 'first-turn',
    showWhenBookable: true,
    searchPages: 2,
  },
};

/** Own keys only — a slug like `constructor` must not resolve to an Object.prototype member. */
export const getLocalPrivateSpecialistsConfig = (slug?: string | null): LocalPrivateSpecialistsConfig | undefined =>
  slug && Object.prototype.hasOwnProperty.call(LOCAL_PRIVATE_SPECIALISTS, slug) ? LOCAL_PRIVATE_SPECIALISTS[slug] : undefined;

export const localPrivateSpecialistsTitle = (config: LocalPrivateSpecialistsConfig) =>
  `سایر پزشکان ${config.expertiseName} ${config.cityName}`;

/** Search filter value for private-office doctors («پزشکان مطبی»). */
export const PRIVATE_OFFICE_RESULT_TYPE = 'پزشکان مطبی';

/** Search query of a config (also the «همه/سایر پزشکان» link): private offices only unless opted out. */
export const searchFilters = (config: LocalPrivateSpecialistsConfig) =>
  config.privateOfficesOnly === false ? {} : { result_type: PRIVATE_OFFICE_RESULT_TYPE };
